export type Validator<T = unknown> = (value: T) => string | null

const DEFAULT_REQUIRED_MESSAGE = "Pflichtfeld"

export function required(message: string = DEFAULT_REQUIRED_MESSAGE): Validator {
  return (value) => {
    if (value === null || value === undefined) return message
    if (typeof value === "string" && value.trim() === "") return message
    if (Array.isArray(value) && value.length === 0) return message
    return null
  }
}

const DEFAULT_URL_MESSAGE = "Ungültige URL"

export function url(message: string = DEFAULT_URL_MESSAGE): Validator {
  return (value) => {
    if (value === null || value === undefined || value === "") return null
    if (typeof value !== "string") return message
    try {
      const parsed = new URL(value)
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return message
      return null
    } catch {
      return message
    }
  }
}

export function minLength(min: number, message?: string): Validator {
  return (value) => {
    if (value === null || value === undefined || value === "") return null
    if (typeof value !== "string") return null
    return value.length >= min ? null : (message ?? `Mindestens ${min} Zeichen`)
  }
}

export function maxLength(max: number, message?: string): Validator {
  return (value) => {
    if (value === null || value === undefined || value === "") return null
    if (typeof value !== "string") return null
    return value.length <= max ? null : (message ?? `Höchstens ${max} Zeichen`)
  }
}

export function intRange(
  bounds: { min?: number; max?: number },
  message?: string
): Validator {
  return (value) => {
    if (value === null || value === undefined || value === "") return null
    if (typeof value !== "number" || !Number.isInteger(value)) {
      return message ?? "Ganze Zahl erforderlich"
    }
    if (bounds.min !== undefined && value < bounds.min) {
      return message ?? `Mindestens ${bounds.min}`
    }
    if (bounds.max !== undefined && value > bounds.max) {
      return message ?? `Höchstens ${bounds.max}`
    }
    return null
  }
}

export function dateRange(
  bounds: { min?: string; max?: string },
  message?: string
): Validator {
  return (value) => {
    if (value === null || value === undefined || value === "") return null
    if (typeof value !== "string") return message ?? "Ungültiges Datum"
    const t = Date.parse(value)
    if (Number.isNaN(t)) return message ?? "Ungültiges Datum"
    if (bounds.min !== undefined) {
      const minT = Date.parse(bounds.min)
      if (!Number.isNaN(minT) && t < minT) {
        return message ?? `Frühestens ${bounds.min}`
      }
    }
    if (bounds.max !== undefined) {
      const maxT = Date.parse(bounds.max)
      if (!Number.isNaN(maxT) && t > maxT) {
        return message ?? `Spätestens ${bounds.max}`
      }
    }
    return null
  }
}

export function composeValidators(...validators: Validator[]): Validator {
  return (value) => {
    for (const validator of validators) {
      const error = validator(value)
      if (error !== null) return error
    }
    return null
  }
}
